// Single source of truth for the PRD conversation lock on the frontend.
//
// This mirrors the backend's `_shared/conversation-lock.ts` contract
// (change 07-conversation-lock-on-approval). The lock is DERIVED from the
// artifact approval status; nothing stores a separate "locked" flag.
//
// The backend returns HTTP 409 with { error, code, reason } on every locked
// write. `code` is the stable, language-independent discriminator the
// frontend must branch on — never branch on the Spanish text. `reason` is the
// user-facing Spanish copy (same value as `error`, kept for back-compat).
//
// Defensive by design: an unknown/missing status or code falls back to the
// unlocked state, so a frontend deployed before the backend rollout keeps its
// current behavior (old backend sends { error } with no `code`).

/**
 * @typedef {'locked_pending_approval'|'locked_approved'|'locked_rejected'} LockCode
 * @typedef {Object} LockState
 * @property {boolean} locked
 * @property {LockCode|null} code
 * @property {string|null} reason  Spanish, user-facing
 * @property {boolean} terminal    true when the lock can never reopen
 */

/** Unlocked singleton — reused so identity checks stay cheap. */
export const UNLOCKED = Object.freeze({
    locked: false,
    code: null,
    reason: null,
    terminal: false,
});

// Reason copy keyed by code. Prefer the backend `reason` when present; this is
// the fallback when the frontend locks optimistically (no backend round-trip
// yet) or when a 409 arrives without a reason.
const REASON_BY_CODE = Object.freeze({
    locked_pending_approval:
        'Tu PRD está en revisión. No puedes editarlo hasta que tu supervisor responda.',
    locked_approved:
        'Tu PRD fue aprobado y pasó a revisión previa del equipo de TI. La conversación queda como historial de solo lectura.',
    locked_rejected:
        'Tu PRD fue rechazado por tu supervisor. La conversación queda como historial de solo lectura.',
});

// A lock is terminal (can never reopen) for approved/rejected. The
// pending_approval lock is REVERSIBLE: when the manager requests changes the
// artifact moves to `changes_requested` and writes succeed again — the
// frontend must NOT cache "locked" forever for that case.
const TERMINAL_CODES = Object.freeze(['locked_approved', 'locked_rejected']);

/**
 * Maps an artifact approval status to its lock state.
 *
 * draft / changes_requested / null (legacy) → unlocked.
 * pending_approval → locked_pending_approval (reversible).
 * approved → locked_approved (terminal).
 * rejected → locked_rejected (terminal).
 *
 * @param {string|null|undefined} status
 * @returns {LockState}
 */
export function lockForArtifactStatus(status) {
    switch (status) {
        case 'pending_approval':
            return lockForCode('locked_pending_approval');
        case 'approved':
            return lockForCode('locked_approved');
        case 'rejected':
            return lockForCode('locked_rejected');
        case 'draft':
        case 'changes_requested':
        default:
            return UNLOCKED;
    }
}

/**
 * Builds a lock state from a backend `code` (as received on a 409). Unknown
 * codes fall back to unlocked so the frontend degrades gracefully against an
 * older backend.
 *
 * @param {LockCode|string|null|undefined} code
 * @param {string|null} [reason]  backend reason; overrides the default copy
 * @returns {LockState}
 */
export function lockForCode(code, reason) {
    if (!code || !REASON_BY_CODE[code]) return UNLOCKED;
    return {
        locked: true,
        code,
        reason: reason || REASON_BY_CODE[code],
        terminal: TERMINAL_CODES.includes(code),
    };
}
